import { EnvelopeIcon, UserCircleIcon, CameraIcon } from '@heroicons/react/20/solid';
import { useState, useEffect, useContext, Suspense, useRef } from 'react';
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { ArrowTurnLeftUpIcon } from '@heroicons/react/24/outline';
import { getProfilePicSrc } from '../utils/mediaUrl';

// Lazy load navigation components
const LazyManagerNav = React.lazy(() => import('../manager/managernav'));
const LazyStaffNav = React.lazy(() => import('./staffnav'));

export default function MyProfile({ onboardMode = false }) {
  const user = useContext(AuthContext);

  const [people, setPeople] = useState([]);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [changePassword, setChangePassword] = useState(false);
  const [currentPasswordInput, setCurrentPasswordInput] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraPerson, setCameraPerson] = useState(null);
  const [showInfoForm, setShowInfoForm] = useState(onboardMode || Number(user?.onboard || 0) === 1);
  const [infoSaving, setInfoSaving] = useState(false);
  const [ecName, setEcName] = useState('');
  const [ecNumber, setEcNumber] = useState('');
  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrPostcode, setAddrPostcode] = useState('');
  const [profileInfo, setProfileInfo] = useState({
    Telephone: '',
    EmergencyContact: '',
    Address: '',
    DOB: '',
    NationalInsuranceNumber: '',
    Medical: '',
    Interests: '',
    Country: '',
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        setProfileLoading(false);
        setProfileError('Your profile session could not be loaded. Please log in again.');
        return;
      }

      try {
        setProfileLoading(true);
        setProfileError('');
        const response = await fetch("https://api.insightos.co.uk/api/myinformationprofile", {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || !data.data) {
          throw new Error(data.message || 'Failed to load your profile');
        }

        setPeople([{
          ...data.data,
          ProfilePic: getProfilePicSrc(data.data) || '/default-profile.png',
        }]);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setProfileError(error.message || 'Failed to load your profile');
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id]);

  useEffect(() => {
    if (!people[0]) return;
    const person = people[0];
    // Parse emergency contact (stored as "Name | Number")
    const ecRaw = person.EmergencyContact || '';
    const ecParts = ecRaw.includes(' | ') ? ecRaw.split(' | ') : [ecRaw, ''];
    setEcName(ecParts[0] || '');
    setEcNumber(ecParts[1] || '');
    // Parse address (stored as "Line1, Line2, City, Postcode")
    const addrParts = (person.Address || '').split(', ').map(s => s.trim());
    setAddrLine1(addrParts[0] || '');
    setAddrLine2(addrParts[1] || '');
    setAddrCity(addrParts[2] || '');
    setAddrPostcode(addrParts[3] || '');
    setProfileInfo({
      Telephone: person.Telephone || '',
      EmergencyContact: person.EmergencyContact || '',
      Address: person.Address || '',
      DOB: person.DOB ? (() => {
        try {
          return new Date(person.DOB).toISOString().slice(0, 10);
        } catch {
          return '';
        }
      })() : '',
      NationalInsuranceNumber: person.NationalInsuranceNumber || '',
      Medical: person.Medical || '',
      Interests: person.Interests || '',
      Country: person.Country || '',
    });
  }, [people]);

  const savePassword = async (e) => {
    e.preventDefault();

    if (!people[0]) {
      setMessage("User not loaded");
      return;
    }

    const userEmail = people[0].Email;

    if (password.length < 8) {
      setMessage("New password must be at least 8 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("New password and confirm password do not match");
      return;
    }

    const payload = {
      Email: userEmail,
      currentPassword: currentPasswordInput,
      newPassword: password,
    };

    try {
      const response = await fetch("https://api.insightos.co.uk/api/newpassword", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setMessage("Password changed successfully");
        setChangePassword(false);
        localStorage.removeItem('token');
        navigate('/');
      } else {
        const errorData = await response.json();
        setMessage("Error changing password: " + errorData.message);
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setMessage("An error occurred while changing the password");
    }
  };

  const saveMyInformation = async (e) => {
    e.preventDefault();
    setInfoSaving(true);
    setMessage('');

    const fusedEC = [ecName.trim(), ecNumber.trim()].filter(Boolean).join(' | ');
    const fusedAddr = [addrLine1, addrLine2, addrCity, addrPostcode].map(s => s.trim()).filter(Boolean).join(', ');
    const payload = { ...profileInfo, EmergencyContact: fusedEC, Address: fusedAddr };
    try {
      const response = await fetch('https://api.insightos.co.uk/api/myinformationprofile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to save your information');
      }

      setMessage('Your information has been saved successfully.');
      if (!onboardMode) {
        setShowInfoForm(false);
      }

      const refreshResponse = await fetch('https://api.insightos.co.uk/api/myinformationprofile', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json();
        if (refreshData?.data) {
          setPeople([{
            ...refreshData.data,
            ProfilePic: getProfilePicSrc(refreshData.data) || '/default-profile.png',
          }]);
        }
      }
    } catch (error) {
      setMessage(error.message || 'Failed to save profile information');
    } finally {
      setInfoSaving(false);
    }
  };

  const mypic = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/adminpic/${user.id}`, {
        method: "PUT",
        credentials: 'include',
        body: formData,
      });
      if (response.ok) {
        window.location.reload();
      } else {
        console.error("Failed to upload file", response.statusText);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // Camera logic
  const openCamera = (person) => {
    setCameraPerson(person);
    setShowCamera(true);
    setTimeout(() => {
      if (videoRef.current) {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(stream => {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          })
          .catch(() => {
            alert("Could not access camera.");
            setShowCamera(false);
          });
      }
    }, 100);
  };

  const closeCamera = () => {
    setShowCamera(false);
    setCameraPerson(null);
    if (videoRef.current && videoRef.current.srcObject) {
      let tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  const takePicture = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      context.drawImage(videoRef.current, 0, 0, 320, 240);
      canvasRef.current.toBlob(blob => {
        if (blob) {
          mypic(blob);
          closeCamera();
        }
      }, 'image/jpeg');
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {profileLoading && (
        <div className="flex min-h-64 items-center justify-center text-sm font-medium text-indigo-700">
          Loading your profile...
        </div>
      )}

      {!profileLoading && profileError && (
        <div className="m-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {profileError}
        </div>
      )}

      {!profileLoading && !profileError && people.map((person) => (
        <div key={person.id} data-component="staff-profile-card">
          {/* Background Image */}
          <div>
            <img
              alt=""
              src="/1.png"
              className="h-32 w-full object-contain bg-gradient-to-r from-indigo-900 from-10% via-sky-500 via-30% to-emerald-500 lg:h-48"
              loading="lazy"
            />
          </div>

          {/* Profile Information */}
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="-mt-12 sm:-mt-16 sm:flex sm:items-end sm:space-x-5">
              <div className="flex">
                {/* Profile Picture */}
                <img
                  alt="Profile"
                  src={person.ProfilePic || '/default-profile.png'}
                  className="size-24 rounded-full ring-4 ring-white sm:size-32"
                  loading="lazy"
                />
                
              </div>

              <div className="mt-6 sm:flex sm:min-w-0 sm:flex-1 sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
                <div className="mt-6 min-w-0 flex-1 sm:hidden md:block">
                  <h1 className="truncate text-2xl font-bold text-gray-900">{person.Name}</h1>
                </div>
                <div className="mt-6 flex flex-col justify-stretch space-y-3 sm:flex-row sm:space-x-4 sm:space-y-0">
                  {/* Message Button */}
                  <button
                    type="button"
                    data-component="staff-profile-action"
                    data-bucket="hr"
                    data-feature="message"
                    className="inline-flex justify-center rounded-md bg-purple-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-purple-200"
                  >
                    <EnvelopeIcon
                      aria-hidden="true"
                      className="-ml-0.5 mr-1.5 size-5 text-purple-400"
                    />
                    <span>Message</span>
                  </button>

                  {/* Location Button */}
                  <button
                    type="button"
                    data-component="staff-profile-action"
                    data-bucket="hr"
                    data-feature="show-location"
                    className="inline-flex inline-block justify-center rounded-md bg-blue-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-blue-200"
                  >
                    <UserCircleIcon
                      aria-hidden="true"
                      className="-ml-0.5 mr-1.5 size-5 text-blue-400"
                    />
                    <span>{person.Location || 'N/A'}</span>
                  </button>
                                   <div className="flex items-center gap-2">

                    <button
                      type="button"
                      data-component="staff-profile-action"
                      data-bucket="hr"
                      data-feature="upload-photo"
                      className="rounded bg-indigo-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 relative"
                    >
                      <ArrowTurnLeftUpIcon className="h-4 w-4 inline-block mr-1" />
                      Change
                      <input
                        type="file"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        style={{ zIndex: 2 }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            mypic(file);
                          }
                        }}
                      />
                      
                    </button>
                    <button
                    type="button"
                    data-component="staff-profile-action"
                    data-bucket="hr"
                    data-feature="open-camera"
                    onClick={() => openCamera(person)}
                    className="rounded bg-emerald-500 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-yellow-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 flex items-center gap-1"
                  >
                    <CameraIcon className="h-4 w-4" />
                    Take Picture
                  </button>
                  </div>
            
                
              </div>
            </div>
              </div>

            {/* Name for Mobile View */}
            <div className="mt-6 hidden min-w-0 flex-1 sm:block md:hidden">
              <h1 className="truncate text-2xl font-bold text-gray-900">{person.Name}</h1>
            </div>

            {/* Profile Information */}
            <div className="overflow-hidden bg-white shadow sm:rounded-lg mt-5">
              <div className="border-t border-gray-100">
                <dl className="divide-y divide-gray-100">
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Role</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{person.Role}</dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Email address</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{person.Email}</dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Telephone</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">{person.Telephone}</dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">My Information</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {!showInfoForm && (
                        <button
                          type="button"
                          onClick={() => setShowInfoForm(true)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs/5 font-medium rounded-lg shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                        >
                          Complete My Information
                        </button>
                      )}

                      {showInfoForm && (
                        <form onSubmit={saveMyInformation} className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Telephone</label>
                            <input
                              type="text"
                              value={profileInfo.Telephone}
                              onChange={(e) => setProfileInfo((prev) => ({ ...prev, Telephone: e.target.value }))}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
                            <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <input
                                type="text"
                                value={ecName}
                                onChange={(e) => setEcName(e.target.value)}
                                placeholder="Full name"
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                              <input
                                type="tel"
                                value={ecNumber}
                                onChange={(e) => setEcNumber(e.target.value)}
                                placeholder="Phone number"
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Address</label>
                            <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <input
                                type="text"
                                value={addrLine1}
                                onChange={(e) => setAddrLine1(e.target.value)}
                                placeholder="Address line 1"
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                              <input
                                type="text"
                                value={addrLine2}
                                onChange={(e) => setAddrLine2(e.target.value)}
                                placeholder="Address line 2"
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                              <input
                                type="text"
                                value={addrCity}
                                onChange={(e) => setAddrCity(e.target.value)}
                                placeholder="City / Town"
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                              <input
                                type="text"
                                value={addrPostcode}
                                onChange={(e) => setAddrPostcode(e.target.value)}
                                placeholder="Postcode"
                                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                            <input
                              type="date"
                              value={profileInfo.DOB}
                              onChange={(e) => setProfileInfo((prev) => ({ ...prev, DOB: e.target.value }))}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">National Insurance Number</label>
                            <input
                              type="text"
                              value={profileInfo.NationalInsuranceNumber}
                              onChange={(e) => setProfileInfo((prev) => ({ ...prev, NationalInsuranceNumber: e.target.value }))}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Do you require Company Support (optional)</label>
                            <textarea
                              rows={3}
                              value={profileInfo.Medical}
                              onChange={(e) => setProfileInfo((prev) => ({ ...prev, Medical: e.target.value }))}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              placeholder="Share any support needs you want us to know."
                            />
                            <p className="mt-1 text-xs text-amber-700">Admin and your direct line manager will see this information.</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Interests</label>
                            <input
                              type="text"
                              value={profileInfo.Interests}
                              onChange={(e) => setProfileInfo((prev) => ({ ...prev, Interests: e.target.value }))}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Country</label>
                            <input
                              type="text"
                              value={profileInfo.Country}
                              onChange={(e) => setProfileInfo((prev) => ({ ...prev, Country: e.target.value }))}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            />
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="submit"
                              disabled={infoSaving}
                              className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                            >
                              {infoSaving ? 'Saving...' : 'Save Information'}
                            </button>
                            {!onboardMode && (
                              <button
                                type="button"
                                onClick={() => setShowInfoForm(false)}
                                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md shadow-sm hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </form>
                      )}
                    </dd>
                  </div>
                  <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-900">Change Password</dt>
                    <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-2 sm:mt-0">
                      {message && <p className="text-red-600">{message}</p>}
                      {!changePassword && (
                        <button
                          data-component="staff-profile-password"
                          data-bucket="hr"
                          data-feature="open-form"
                          onClick={() => setChangePassword(true)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs/5 font-medium rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-auto"
                        >
                          Change Password
                        </button>
                      )}
                      {changePassword && (
                        <form data-component="staff-profile-password-form" onSubmit={savePassword} className="mt-6">
                          <div className="mt-2">
                            <label
                              htmlFor="currentPassword"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Current Password
                            </label>
                            <input
                              data-component="staff-profile-password-field"
                              data-bucket="hr"
                              data-feature="current"
                              type="password"
                              name="currentPassword"
                              id="currentPassword"
                              autoComplete="current-password"
                              value={currentPasswordInput}
                              onChange={(e) => setCurrentPasswordInput(e.target.value)}
                              required
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm focus:outline-none"
                            />
                          </div>

                          <div className="mt-2">
                            <label
                              htmlFor="password"
                              className="block text-sm font-medium text-gray-700"
                            >
                              New Password
                            </label>
                            <input
                              data-component="staff-profile-password-field"
                              data-bucket="hr"
                              data-feature="new"
                              type="password"
                              name="password"
                              id="password"
                              autoComplete="new-password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm focus:outline-none"
                            />
                          </div>

                          <div className="mt-2">
                            <label
                              htmlFor="confirmPassword"
                              className="block text-sm font-medium text-gray-700"
                            >
                              Confirm Password
                            </label>
                            <input
                              data-component="staff-profile-password-field"
                              data-bucket="hr"
                              data-feature="confirm"
                              type="password"
                              name="confirmPassword"
                              id="confirmPassword"
                              autoComplete="new-password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              required
                              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm focus:outline-none"
                            />
                          </div>

                          <div className="mt-4">
                            <button
                              data-component="staff-profile-password"
                              data-bucket="hr"
                              data-feature="submit"
                              type="submit"
                              className="inline-flex items-center px-4 py-2 bg-emerald-500 text-white rounded-md shadow-sm hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                            >
                              Submit
                            </button>
                            <button
                              data-component="staff-profile-password"
                              data-bucket="hr"
                              data-feature="cancel"
                              type="button"
                              onClick={() => setChangePassword(false)}
                              className="ml-2 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Camera Modal */}
          {showCamera && cameraPerson && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
              <div className="bg-white rounded-lg p-6 flex flex-col items-center">
                <video ref={videoRef} width={320} height={240} autoPlay className="rounded" />
                <canvas ref={canvasRef} width={320} height={240} style={{ display: 'none' }} />
                <div className="mt-4 flex gap-2">
                  <button
                    data-component="staff-profile-camera"
                    data-bucket="hr"
                    data-feature="capture"
                    onClick={takePicture}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Take Picture
                  </button>
                  <button
                    data-component="staff-profile-camera"
                    data-bucket="hr"
                    data-feature="close"
                    onClick={closeCamera}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </Suspense>
  );
}
